import React, { useContext, useState, useRef, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import bg from "../../../assets/svgs/moreServiceBg.svg";
import { Link } from "react-router-dom";

type MoreServiceType = {
  items: {
    icon: string;
    name: string;
    des: string;
    path: string;
  }[];
};

const NewCommonMoreItService = ({ items }: MoreServiceType) => {
  const dataContext = useContext(AuthContext);
  const [hoverStates, setHoverStates] = useState<{[key: number]: {
    x: number, 
    y: number, 
    isHovering: boolean,
    currentX: number,
    currentY: number
  }}>({});

  const animationRefs = useRef<{[key: number]: number}>({});
  const initialPositionRefs = useRef<{[key: number]: {x: number, y: number}}>({});

  const handleMouseMove = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const targetX = e.clientX - rect.left;
    const targetY = e.clientY - rect.top;

    // Ensure initial position is set only once when hovering starts
    if (!initialPositionRefs.current[index]) {
      initialPositionRefs.current[index] = { 
        x: targetX, 
        y: targetY 
      };
    }

    // Cancel any existing animation
    if (animationRefs.current[index]) {
      cancelAnimationFrame(animationRefs.current[index]);
    }

    // Start a new smooth animation
    const animate = () => {
      setHoverStates(prev => {
        const current = prev[index] || { 
          currentX: initialPositionRefs.current[index].x, 
          currentY: initialPositionRefs.current[index].y 
        };
        
        // Smooth interpolation (easing)
        const newX = current.currentX + (targetX - current.currentX) * 0.1;
        const newY = current.currentY + (targetY - current.currentY) * 0.1;

        // Check if we're close enough to stop animation
        const isCloseEnough = 
          Math.abs(targetX - newX) < 1 && 
          Math.abs(targetY - newY) < 1;

        return {
          ...prev,
          [index]: {
            x: targetX,
            y: targetY,
            isHovering: true,
            currentX: isCloseEnough ? targetX : newX,
            currentY: isCloseEnough ? targetY : newY
          }
        };
      });

      // Continue animation if not close enough
      const current = hoverStates[index];
      if (current && 
          (Math.abs(targetX - current.currentX) > 1 || 
           Math.abs(targetY - current.currentY) > 1)) {
        animationRefs.current[index] = requestAnimationFrame(animate);
      }
    };

    // Start the animation
    animationRefs.current[index] = requestAnimationFrame(animate);
  };

  const handleMouseLeave = (index: number) => {
    // Cancel any existing animation
    if (animationRefs.current[index]) {
      cancelAnimationFrame(animationRefs.current[index]);
    }

    // Reset initial position
    delete initialPositionRefs.current[index];

    setHoverStates(prev => ({
      ...prev,
      [index]: {
        x: 0,
        y: 0,
        isHovering: false,
        currentX: 0,
        currentY: 0
      }
    }));
  };

  useEffect(() => {
    // Cleanup function to cancel all animations on unmount
    return () => {
      Object.values(animationRefs.current).forEach(cancelAnimationFrame);
    };
  }, []);

  if (!dataContext) return null;
  const { theme } = dataContext;

  return (
    <div
      className={`${
        theme === "dark" ? "bg-white text-black" : "bg-black text-white"
      } transition-all duration-500 overflow-hidden`}
    >
      <div className="max-w-screen-2xl mx-auto px-5 sm:px-20 py-8 sm:py-28 relative">
        <div className="grid sm:grid-cols-3 gap-5 sm:gap-10 mx-auto">
          {items.map((item, index) => (
            <Link
              to={item.path}
              className={`${
                theme === "dark" ? "bg-secondary" : "card-gradient"
              } p-[1px] rounded-2xl cursor-pointer relative z-10 transition-all duration-500`}
              key={index}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <div
                className={`${
                  theme === "dark"
                    ? "bg-white hover:shadow-[inset_0px_0px_20px_#f20b97]"
                    : "bg-[#141414] hover:shadow-[inset_0px_0px_60px_#8D19DA]"
                } py-6 px-4 h-full rounded-2xl flex flex-col justify-center items-center transition-all duration-500 relative`}
                onMouseMove={(e) => handleMouseMove(index, e)}
                onMouseLeave={() => handleMouseLeave(index)}
              >
                <div className="bg-secondary size-20 rounded-full flex justify-center items-center mb-10">
                  <img src={item.icon} alt="icon" className="" />
                </div>
                <div className="flex flex-col items-center">
                  <h4 className="bricolage-grotesque-semibold text-2xl mb-4 sm:w-[70%] text-center">
                    {item.name}
                  </h4>
                  <p className="font-gtAmerica mb-4 text-center sm:w-[80%]">
                    {item.des}
                  </p>
                </div>
                {hoverStates[index]?.isHovering && (
                  <div
                  className={`${
                    theme === "dark"
                      ? "bg-primary text-white"
                      : "bg-white text-black"
                  } absolute transition-opacity duration-300 opacity-100 pointer-events-none size-16 flex justify-center items-center rounded-full text-center bricolage-grotesque-semibold`}
                  style={{
                    left: `${hoverStates[index]?.currentX || 0}px`,
                    top: `${hoverStates[index]?.currentY || 0}px`,
                    transform: "translate(-50%, 50%)",
                  }}
                >
                  View
                </div>
                )}
              </div>
            </Link>
          ))}
        </div>
        <img
          className={`${
            theme === "dark" ? "hidden" : ""
          } absolute top-0 right-0`}
          src={bg}
        />
        <img
          className={`${
            theme === "dark" ? "hidden" : ""
          } absolute top-0 left-0 rotate-180`}
          src={bg}
        />
      </div>
    </div>
  );
};

export default NewCommonMoreItService;


